import { ArrowRight, Bike, Gauge, Mail, Phone, Sparkles, Wrench } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import SiempriaCredit from "../components/SiempriaCredit";
import { LANGS } from "../components/Header";
import { useLang } from "../i18n";
import { WHATSAPP_NUMBER } from "../config";

const sparks = [
  { left: "18%", top: "18%", delay: "0s" },
  { left: "78%", top: "22%", delay: ".7s" },
  { left: "16%", top: "72%", delay: "1.1s" },
  { left: "82%", top: "68%", delay: ".35s" },
];

function MechanicArt() {
  return (
    <div className="relative w-full max-w-md aspect-square mx-auto" data-testid="maintenance-mechanic" aria-hidden="true">
      <div className="absolute inset-x-8 bottom-10 h-24 rounded-[50%] border-[10px] border-zinc-700 bg-black/40" />
      <div className="absolute left-10 bottom-16 w-32 h-32 rounded-full border-[12px] border-zinc-600 animate-[spin_9s_linear_infinite]" />
      <div className="absolute right-10 bottom-16 w-32 h-32 rounded-full border-[12px] border-zinc-600 animate-[spin_9s_linear_infinite_reverse]" />
      <div className="absolute left-1/2 bottom-36 -translate-x-1/2 w-40 h-24 rounded-t-[90px] bg-brand border-4 border-topacio-ink shadow-[6px_6px_0_0_#111]" />
      <div className="absolute left-1/2 bottom-56 -translate-x-1/2 w-28 h-28 rounded-full bg-[#F5C58B] border-4 border-topacio-ink overflow-hidden">
        <div className="absolute -top-5 left-0 right-0 h-14 rounded-b-[40px] bg-topacio-red border-b-4 border-topacio-ink" />
        <div className="absolute top-9 left-4 right-4 h-7 rounded-full bg-zinc-900 border-2 border-topacio-ink" />
        <div className="absolute top-[4.8rem] left-1/2 -translate-x-1/2 w-10 h-4 rounded-b-full bg-topacio-ink" />
      </div>
      <div className="absolute left-[23%] bottom-44 w-24 h-5 bg-[#F5C58B] border-4 border-topacio-ink rounded-full rotate-[24deg] origin-right" />
      <div className="absolute right-[18%] bottom-48 w-28 h-5 bg-[#F5C58B] border-4 border-topacio-ink rounded-full -rotate-[28deg] origin-left" />
      <div className="absolute right-[9%] bottom-56 text-brand animate-[wrench_1.8s_ease-in-out_infinite]">
        <Wrench className="w-20 h-20 drop-shadow-[4px_4px_0_#111]" />
      </div>
      <div className="absolute left-[15%] top-[16%] text-brand animate-bounce"><Sparkles className="w-8 h-8" /></div>
      <div className="absolute right-[12%] top-[20%] text-topacio-green animate-pulse"><Gauge className="w-9 h-9" /></div>
      <div className="absolute left-[14%] bottom-[7rem] text-white/70 animate-pulse"><Bike className="w-12 h-12" /></div>
      <style>{`@keyframes wrench {0%,100%{transform:rotate(-14deg)}50%{transform:rotate(22deg) translateY(-8px)}}`}</style>
    </div>
  );
}

export default function MaintenancePage() {
  const { lang, setLang, t } = useLang();
  const m = t.maintenance;
  return (
    <div className="min-h-screen bg-[#080808] text-white relative overflow-hidden" data-testid="maintenance-page">
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "54px 54px" }} />
      {sparks.map((s, i) => (
        <span key={i} className="absolute w-2 h-2 rounded-full bg-brand animate-ping" style={{ left: s.left, top: s.top, animationDelay: s.delay }} />
      ))}
      <header className="relative max-w-6xl mx-auto px-6 py-6 flex items-center justify-between gap-4 flex-wrap">
        <img src="/logo-topacio.jpg" alt="Rent a Moto Topacio" className="h-14 w-auto rounded-xl border-2 border-brand shadow-[4px_4px_0_0_#FFCC00]" />
        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-lg border-2 border-white/20 overflow-hidden" data-testid="maintenance-langs">
            {LANGS.map(({ id, label, Flag }) => (
              <button key={id} data-testid={`maintenance-lang-${id}`} title={label} aria-label={label} onClick={() => setLang(id)}
                className={`px-2.5 py-2 flex items-center transition-all duration-200 ${lang === id ? "bg-brand" : "opacity-50 hover:opacity-100 hover:bg-white/10"}`}>
                <Flag />
              </button>
            ))}
          </div>
          <span className="badge-brutal bg-topacio-red text-white !text-[10px]"><Wrench className="w-3.5 h-3.5" /> {m.badge}</span>
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-6 pb-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center min-h-[calc(100vh-170px)]">
        <section>
          <p className="eyebrow !text-brand mb-4">{m.eyebrow}</p>
          <h1 className="font-display font-black text-4xl sm:text-6xl leading-[0.95] tracking-tight">
            {m.titleA}<br />{m.titleB}
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg font-semibold mt-6 max-w-xl leading-relaxed">
            {m.sub}
          </p>
          <a href="tel:+34922752605" data-testid="maintenance-phone"
            className="group block mt-8 max-w-xl rounded-2xl border-2 border-brand bg-brand/10 px-6 py-5 shadow-[6px_6px_0_0_#FFCC00] hover:-translate-y-0.5 hover:shadow-[8px_8px_0_0_#FFCC00] transition-[transform,box-shadow]">
            <p className="text-[11px] uppercase tracking-[0.25em] font-extrabold text-brand flex items-center gap-2">
              <Phone className="w-4 h-4" /> {m.phoneLabel}
            </p>
            <p className="font-display font-black text-3xl sm:text-5xl tracking-tight text-white mt-2 group-hover:text-brand transition-colors whitespace-nowrap">
              922 75 26 05
            </p>
            <p className="text-zinc-400 text-xs font-semibold mt-1.5">{m.hours}</p>
          </a>
          <div className="flex flex-wrap gap-3 mt-6">
            <a data-testid="maintenance-whatsapp" href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer"
              className="btn-primary !bg-brand !text-topacio-ink">
              <FaWhatsapp className="w-4 h-4" /> {m.whatsapp} <ArrowRight className="w-4 h-4" />
            </a>
            <a data-testid="maintenance-email" href="mailto:rent@rentamotenerife.es"
              className="btn-secondary !bg-transparent !border-white/25 !text-white !shadow-[4px_4px_0_0_rgba(255,255,255,0.25)] hover:!bg-white/10 normal-case !tracking-normal">
              <Mail className="w-4 h-4" /> rent@rentamototenerife.es
            </a>
          </div>
          <div className="flex flex-wrap justify-center sm:justify-start gap-2.5 mt-10 max-w-lg">
            {m.checking.map((item, i) => (
              <div key={i} data-testid={`maintenance-badge-${i}`}
                className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5 flex items-center gap-2 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse shrink-0" />
                <p className="font-display font-bold text-xs sm:text-sm leading-none">{item}</p>
              </div>
            ))}
          </div>
        </section>
        <section className="relative">
          <div className="absolute inset-8 rounded-full bg-brand/10 blur-3xl" />
          <MechanicArt />
        </section>
      </main>

      <footer className="relative border-t border-white/10 py-4 px-6 flex items-center justify-center text-center">
        <SiempriaCredit dark className="!py-0" />
      </footer>
    </div>
  );
}
